/**
 * 鼠标改键页：按键热点 / 标签锚点（相对 .mouse-visualizer 的百分比定位）
 * 与 mouse-management changeKey/mouseChangeKeyLayouts 保持一致，供社区预览复用。
 */

export type MouseChangeKeySpotId =
  | "left"
  | "middle"
  | "right"
  | "side1"
  | "side2"
  | "scrollUp"
  | "scrollDown"
  | "dpi";

export interface MouseChangeKeySpot {
  id: MouseChangeKeySpotId;
  position: { x: number; y: number };
  labelPosition: { x: number; y: number };
  align?: "left" | "right";
}

export const DEFAULT_MOUSE_CHANGEKEY_LAYOUT: MouseChangeKeySpot[] = [
  {
    id: "left",
    position: { x: 30, y: 12 },
    labelPosition: { x: 0, y: -12 },
  },
  {
    id: "middle",
    position: { x: 52, y: 22 },
    labelPosition: { x: 52, y: -12 },
  },
  {
    id: "right",
    position: { x: 76, y: 12 },
    labelPosition: { x: 104, y: -12 },
  },
  {
    id: "side2",
    position: { x: 8, y: 38 },
    labelPosition: { x: -16, y: 38 },
    align: "right",
  },
  {
    id: "side1",
    position: { x: 8, y: 58 },
    labelPosition: { x: -16, y: 58 },
    align: "right",
  },
];

/** 展立：在默认布局上增加滚轮上 / 滚轮下 / DPI（键位参照 demo） */
export const ZHANLI_MOUSE_CHANGEKEY_LAYOUT: MouseChangeKeySpot[] = [
  {
    id: "left",
    position: { x: 30, y: 12 },
    labelPosition: { x: 0, y: -12 },
  },
  {
    id: "right",
    position: { x: 76, y: 12 },
    labelPosition: { x: 104, y: -12 },
  },
  {
    id: "middle",
    position: { x: 52, y: 21 },
    labelPosition: { x: 52, y: -12 },
  },
  {
    id: "scrollUp",
    position: { x: 58, y: 15 },
    labelPosition: { x: 132, y: 22 },
  },
  {
    id: "scrollDown",
    position: { x: 52, y: 29 },
    labelPosition: { x: 132, y: 40 },
  },
  {
    id: "dpi",
    position: { x: 78, y: 48 },
    labelPosition: { x: 132, y: 64 },
  },
  {
    id: "side2",
    position: { x: 8, y: 38 },
    labelPosition: { x: -16, y: 38 },
    align: "right",
  },
  {
    id: "side1",
    position: { x: 8, y: 58 },
    labelPosition: { x: -16, y: 58 },
    align: "right",
  },
];

/** 匠盟：默认 5 键 + DPI（index 5，对齐 deviceconfig / 匠盟 demo） */
export const JIANGMENG_MOUSE_CHANGEKEY_LAYOUT: MouseChangeKeySpot[] = [
  ...DEFAULT_MOUSE_CHANGEKEY_LAYOUT,
  {
    id: "dpi",
    position: { x: 78, y: 58 },
    labelPosition: { x: 132, y: 58 },
  },
];

const LEFT_HAND_MOUSE_LAYOUT: MouseChangeKeySpot[] = [
  {
    id: "left",
    position: { x: 40, y: 21 },
    labelPosition: { x: 10, y: -12 },
  },
  {
    id: "middle",
    position: { x: 60, y: 27 },
    labelPosition: { x: 60, y: -12 },
  },
  {
    id: "right",
    position: { x: 80, y: 21 },
    labelPosition: { x: 110, y: -12 },
  },
  {
    id: "side2",
    position: { x: 18, y: 43 },
    labelPosition: { x: -18, y: 43 },
    align: "right",
  },
  {
    id: "side1",
    position: { x: 20, y: 60 },
    labelPosition: { x: -18, y: 60 },
    align: "right",
  },
];

/** 匠盟鼠标 deviceId（与 supportedDevices 对齐） */
const JIANGMENG_DEVICE_IDS = new Set([
  "z7",
  "s9ultrav2",
  "s9prov2",
  "x23zhuoyue",
  "s9max2",
  "z7master",
  "s9v2master",
  "s9ultimate",
  "s9v2master+",
  "s9v2ultimate",
  "a8wbultra",
  "s9v2turbo",
  "alpha6",
]);

const LAYOUT_BY_DEVICE_ID: Record<string, MouseChangeKeySpot[]> = {
  a7: LEFT_HAND_MOUSE_LAYOUT,
  a7se: LEFT_HAND_MOUSE_LAYOUT,
  a7pro: LEFT_HAND_MOUSE_LAYOUT,
  g2sea: LEFT_HAND_MOUSE_LAYOUT,
  alpha7: ZHANLI_MOUSE_CHANGEKEY_LAYOUT,
  z7max: ZHANLI_MOUSE_CHANGEKEY_LAYOUT,
};

/** 侧键 spot → 设备按键 index：side1(下)=3 后退，side2(上)=4 前进 */
export function getMouseSideButtonIndex(
  _deviceId: string,
  spotId: "side1" | "side2"
): number {
  return spotId === "side1" ? 3 : 4;
}

/** 改键页侧键默认文案（与 getMouseSideButtonIndex 对齐） */
export function getMouseSideButtonDefaultLabels(
  _deviceId: string,
  t: (key: string) => string
): { side1: string; side2: string } {
  return {
    side1: t("mouse.changeKey.backward"),
    side2: t("mouse.changeKey.forward"),
  };
}

/** 改键页物理位锁定：deviceId → 不可改的 spot id */
const FIXED_MOUSE_BUTTONS: Record<string, MouseChangeKeySpotId[]> = {
  x23lcarbon: ["left"],
};

export function getFixedMouseButtonIds(
  deviceId: string
): MouseChangeKeySpotId[] {
  const key = deviceId?.trim?.() ? deviceId.trim().toLowerCase() : "";
  return FIXED_MOUSE_BUTTONS[key] ?? [];
}

export function isMouseButtonFixed(
  deviceId: string,
  buttonId: MouseChangeKeySpotId
): boolean {
  return getFixedMouseButtonIds(deviceId).includes(buttonId);
}

export function getMouseChangeKeyLayout(
  deviceId: string,
  options?: { provider?: string }
): MouseChangeKeySpot[] {
  const key = deviceId?.trim?.() ? deviceId.trim().toLowerCase() : "";
  if (key && LAYOUT_BY_DEVICE_ID[key]) {
    return LAYOUT_BY_DEVICE_ID[key];
  }
  const provider = options?.provider?.trim?.().toLowerCase?.() ?? "";
  if (
    provider === "jiangmeng" ||
    provider === "yxt" ||
    key === "e7v2max" ||
    (key && JIANGMENG_DEVICE_IDS.has(key))
  ) {
    return JIANGMENG_MOUSE_CHANGEKEY_LAYOUT;
  }
  return DEFAULT_MOUSE_CHANGEKEY_LAYOUT;
}
